import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertCircle, AlertTriangle, CheckCircle, Clock, Flag, User, Calendar } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SafetyInvestigationForm from "@/components/admin/SafetyInvestigationForm";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface SafetyReport {
  id: string;
  user_id: string;
  issue_type: string;
  severity: string;
  status: string;
  description: string;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  resolution_notes: string | null;
  conversation_id: string | null;
  contact_permission: boolean;
  user_profile?: {
    display_name: string | null;
  } | null;
  assigned_to?: string | null;
  investigation_deadline?: string | null;
  investigation_status?: string | null;
}

export function SafetyInvestigationsManager() {
  const [reports, setReports] = useState<SafetyReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<SafetyReport | null>(null);
  const [showInvestigationForm, setShowInvestigationForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'investigating' | 'completed'>('pending');
  const [staffMembers, setStaffMembers] = useState<Array<{ id: string; email: string; display_name: string }>>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<string>("");
  const [assignmentNotes, setAssignmentNotes] = useState("");

  useEffect(() => {
    loadReports();
    loadStaffMembers();
  }, [filter]);

  const loadReports = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('adverse_events')
        .select(`
          *
        `)
        .order('created_at', { ascending: false });

      // Get profile data separately
      const { data: reportsData, error: reportsError } = await query;
      if (reportsError) throw reportsError;

      // Fetch user profiles
      const userIds = reportsData?.map(r => r.user_id) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIds);

      // Combine data
      const reportsWithProfiles = reportsData?.map(report => ({
        ...report,
        user_profile: profilesData?.find(p => p.id === report.user_id) || null
      }));

      setReports(reportsWithProfiles || []);
      return;

      if (filter !== 'all') {
        if (filter === 'pending') {
          query = query.eq('status', 'pending');
        } else if (filter === 'investigating') {
          query = query.eq('status', 'investigating');
        } else if (filter === 'completed') {
          query = query.eq('status', 'reviewed');
        }
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load safety reports');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStaffMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('staff_department_access')
        .select(`
          user_id,
          profiles:user_id (
            display_name,
            email
          )
        `);

      if (error) throw error;

      const uniqueStaff = Array.from(
        new Map(
          data?.map(item => [
            item.user_id,
            {
              id: item.user_id,
              email: (item.profiles as any)?.email || '',
              display_name: (item.profiles as any)?.display_name || 'Unknown Staff'
            }
          ])
        ).values()
      );

      setStaffMembers(uniqueStaff);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleAssignInvestigation = async (reportId: string) => {
    if (!selectedAssignee) {
      toast.error('Please select a staff member');
      return;
    }

    try {
      // Calculate deadline based on severity
      const report = reports.find(r => r.id === reportId);
      const daysToAdd = report?.severity === 'critical' ? 1 : report?.severity === 'high' ? 2 : 5;
      const deadline = new Date();
      deadline.setDate(deadline.getDate() + daysToAdd);

      const { error } = await supabase
        .from('adverse_events')
        .update({
          status: 'investigating',
          assigned_to: selectedAssignee,
          investigation_deadline: deadline.toISOString(),
          resolution_notes: assignmentNotes || null
        })
        .eq('id', reportId);

      if (error) throw error;

      toast.success('Investigation assigned successfully');
      setAssignDialogOpen(false);
      setSelectedAssignee("");
      setAssignmentNotes("");
      loadReports();
    } catch (error) {
      console.error('Error assigning investigation:', error);
      toast.error('Failed to assign investigation');
    }
  };

  const handleStartInvestigation = (report: SafetyReport) => {
    setSelectedReport(report);
    setShowInvestigationForm(true);
  };

  const handleInvestigationComplete = async () => {
    setShowInvestigationForm(false);
    setSelectedReport(null);
    await loadReports();
    toast.success('Investigation completed');
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'medium':
        return <Flag className="h-4 w-4 text-primary" />;
      default:
        return <Flag className="h-4 w-4 text-secondary" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case 'investigating':
        return <Badge className="gap-1"><AlertCircle className="h-3 w-3" />Investigating</Badge>;
      case 'reviewed':
        return <Badge variant="secondary" className="gap-1"><CheckCircle className="h-3 w-3" />Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const isOverdue = (report: SafetyReport) => {
    if (!report.investigation_deadline || report.status === 'reviewed') return false;
    return new Date(report.investigation_deadline) < new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reports.filter(r => r.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Under Investigation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reports.filter(r => r.status === 'investigating').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {reports.filter(r => isOverdue(r)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Critical Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {reports.filter(r => r.severity === 'critical' && r.status !== 'reviewed').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Safety Reports & Investigations</CardTitle>
              <CardDescription>
                Manage and investigate user safety reports per SOP requirements. Users submit via Report Safety Concern button. Email confirmations are automatically sent.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pending')}
              >
                Pending
              </Button>
              <Button
                variant={filter === 'investigating' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('investigating')}
              >
                Investigating
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('completed')}
              >
                Completed
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reports.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No safety reports found
              </div>
            ) : (
              reports.map((report) => (
                <Card key={report.id} className={isOverdue(report) ? 'border-destructive' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          {getSeverityIcon(report.severity)}
                          <Badge variant={getSeverityColor(report.severity) as any}>
                            {report.severity.toUpperCase()}
                          </Badge>
                          {getStatusBadge(report.status)}
                          {isOverdue(report) && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Overdue
                            </Badge>
                          )}
                        </div>

                        <div>
                          <p className="font-medium">{report.issue_type}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {report.description}
                          </p>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {report.user_profile?.display_name || 'Anonymous'}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(report.created_at).toLocaleDateString()}
                          </div>
                          {report.investigation_deadline && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Due: {new Date(report.investigation_deadline).toLocaleDateString()}
                            </div>
                          )}
                        </div>

                        {report.resolution_notes && (
                          <div className="mt-2 p-3 bg-muted rounded-md">
                            <p className="text-sm font-medium mb-1">Investigation Notes:</p>
                            <p className="text-sm">{report.resolution_notes}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        {report.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedReport(report);
                                setAssignDialogOpen(true);
                              }}
                            >
                              Assign
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartInvestigation(report)}
                            >
                              Investigate
                            </Button>
                          </>
                        )}
                        {report.status === 'investigating' && (
                          <Button
                            size="sm"
                            onClick={() => handleStartInvestigation(report)}
                          >
                            Continue
                          </Button>
                        )}
                        {report.status === 'reviewed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedReport(report);
                              setShowInvestigationForm(false);
                            }}
                          >
                            View Details
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Investigation</DialogTitle>
            <DialogDescription>
              Assign this safety report to a staff member for investigation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Assign To</label>
              <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffMembers.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.display_name} ({staff.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Assignment Notes</label>
              <Textarea
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                placeholder="Add any specific instructions or context..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => selectedReport && handleAssignInvestigation(selectedReport.id)}>
                Assign Investigation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Investigation Form Dialog */}
      {showInvestigationForm && selectedReport && (
        <Dialog open={showInvestigationForm} onOpenChange={setShowInvestigationForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Safety Investigation</DialogTitle>
              <DialogDescription>
                Complete the investigation per SOP requirements
              </DialogDescription>
            </DialogHeader>
            <SafetyInvestigationForm
              reportId={selectedReport.id}
              issueType={selectedReport.issue_type}
              severity={selectedReport.severity}
              description={selectedReport.description}
              conversationId={selectedReport.conversation_id}
              onComplete={handleInvestigationComplete}
              onCancel={() => setShowInvestigationForm(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
